<template>
    <div class="view home-view">
        <img
            :src="logo"
            alt="TopScale Logo"
            class="home-logo"
        />
        <AppHeader :HeaderTitle="`Welcome to TopScale, ${userStore.username}!`">
            <template #actions>
            </template>
        </AppHeader>
        <div class="card home-card">
            <!-- Actions -->
            <div v-if="userStore.username">
                <div class="button-group">
                    <button
                        @click="createLobby"
                        class="btn btn-success w-full action-button"
                        :disabled="isCreating"
                    >
                        <span v-if="!isCreating">🎯 Create New Lobby</span>
                        <span v-else>Creating...</span>
                    </button>

                    <button
                        @click="showJoinModal = true"
                        class="btn btn-primary w-full action-button"
                    >
                        🚪 Join Existing Lobby
                    </button>
                </div>
            </div>
        </div>

        <!-- Join Lobby Modal -->
        <Teleport to="body">
            <div v-if="showJoinModal" class="modal-overlay" @click="showJoinModal = false">
            <div class="card modal-card" @click.stop>
                <h3 class="mb-4">Join Lobby</h3>
                <label class="label">Enter lobby code:</label>
                <input
                    v-model="joinCode"
                    type="text"
                    placeholder="e.g. ABC123"
                    class="mb-4 uppercase-input"
                    @keyup.enter="joinLobby"
                    autofocus
                />
                <div class="button-row">
                    <button class="btn btn-primary flex-1" @click="joinLobby" :disabled="!joinCode.trim() || isJoining">
                        <span v-if="!isJoining">Join</span>
                        <span v-else>Joining...</span>
                    </button>
                    <button class="btn btn-secondary flex-1" @click="showJoinModal = false">Cancel</button>
                </div>
            </div>
        </div>
        </Teleport>
        <AppFooter
            :joinCode="joinCode"
            @handleNameSaved="handleNameSaved"
        />
    </div>
</template>

<script setup>
    import { ref, onMounted } from 'vue'
    import { useRouter, useRoute } from 'vue-router'
    import { useUserStore } from '../stores/user'
    import { useSocketStore } from '../stores/socket'
    import { useLobbyStore } from '../stores/lobby'
    import AppHeader from '../components/AppHeader.vue'
    import AppFooter from '../components/AppFooter.vue'
    import logo from '../../../icons/nav_brand.svg'
    import LoginModal from '../components/LoginModal.vue'

    const router = useRouter()
    const route = useRoute()
    const userStore = useUserStore()
    const socketStore = useSocketStore()
    const lobbyStore = useLobbyStore()

    const showJoinModal = ref(false)
    const joinCode = ref('')
    const isCreating = ref(false)
    const isJoining = ref(false)

    onMounted(() => {
    // Check if there's a join code in the query parameter
    if (route.query.join) {
        joinCode.value = route.query.join.toUpperCase()

        // If user already has a username, auto-join
        if (userStore.username) {
        console.log('🔗 Auto-joining lobby from URL:', joinCode.value)
        router.push(`/${joinCode.value}`)
        } else {
        // Show the name changer modal with join hint
        console.log('💡 Join code detected, showing name modal:', joinCode.value)
        }
    }
    })

    function handleNameSaved(data) {
    console.log('✅ Name saved:', data.username)

    // If there's a join code, auto-join the lobby
    if (data.joinCode) {
        console.log('🚀 Auto-joining lobby:', data.joinCode)
        router.push(`/${data.joinCode}`)
    }
    }

    function createLobby() {
    if (!socketStore.socket?.connected) {
        alert('Not connected to server. Please refresh the page.')
        return
    }

    isCreating.value = true
    socketStore.createLobby(userStore.username)

    // Listen for lobby created event
    const onLobbyCreated = (data) => {
        isCreating.value = false
        // Navigate to /:code instead of /lobby/:code
        router.push(`/${data.code}`)
        socketStore.socket.off('lobby:created', onLobbyCreated)
    }

    socketStore.socket.on('lobby:created', onLobbyCreated)

    // Timeout after 5 seconds
    setTimeout(() => {
        if (isCreating.value) {
        isCreating.value = false
        alert('Failed to create lobby. Please try again.')
        socketStore.socket.off('lobby:created', onLobbyCreated)
        }
    }, 5000)
    }

    function joinLobby() {
    if (!joinCode.value.trim()) return

    const code = joinCode.value.trim().toUpperCase()
    isJoining.value = true

    socketStore.joinLobby(code, userStore.username)

    // Listen for successful join
    const onJoined = (data) => {
        isJoining.value = false
        showJoinModal.value = false
        // Navigate to /:code
        router.push(`/${code}`)
        socketStore.socket.off('lobby:joined', onJoined)
        socketStore.socket.off('lobby:join-error', onError)
    }

    const onError = (data) => {
        isJoining.value = false
        alert(data.message || 'Failed to join lobby')
        socketStore.socket.off('lobby:joined', onJoined)
        socketStore.socket.off('lobby:join-error', onError)
    }

    socketStore.socket.on('lobby:joined', onJoined)
    socketStore.socket.on('lobby:join-error', onError)

    // Timeout after 5 seconds
    setTimeout(() => {
        if (isJoining.value) {
            isJoining.value = false
            alert('Failed to join lobby. Please check the code and try again.')
            socketStore.socket.off('lobby:joined', onJoined)
            socketStore.socket.off('lobby:join-error', onError)
        }
    }, 5000)
}
</script>
